/**
 * TryoutListDetailView — virtual-scrolled lead table with bulk-ops + CSV export.
 * Mounted via App.tsx routing for /admin/tryout-lists/:id.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ArrowLeft, Download, RefreshCw, Trash2, Search, Loader2,
  Pause, Play, MoreVertical, Sparkles,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { TryoutLeadDrawer, type DrawerToken } from './TryoutLeadDrawer';
import { buildInstantlyExportCsv } from '@/lib/csv/leadCsvExport';

interface ListMeta {
  id: string; name: string; campaign_tag: string | null; source: string;
  notes: string | null; created_at: string;
}

interface Props {
  listId: string;
  onBack: () => void;
}

export const TryoutListDetailView: React.FC<Props> = ({ listId, onBack }) => {
  const [list, setList] = useState<ListMeta | null>(null);
  const [tokens, setTokens] = useState<DrawerToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [pStatusFilter, setPStatusFilter] = useState<string>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [drawerToken, setDrawerToken] = useState<DrawerToken | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    const [{ data: meta }, { data: rows }] = await Promise.all([
      supabase.from('tryout_lead_lists').select('*').eq('id', listId).single(),
      supabase.from('tryout_tokens').select('*').eq('list_id', listId).order('created_at', { ascending: true }),
    ]);
    setList(meta as ListMeta | null);
    setTokens((rows ?? []) as DrawerToken[]);
    setLoading(false);
  }, [listId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tokens.filter((t) => {
      if (statusFilter === 'active' && !t.is_active) return false;
      if (statusFilter === 'inactive' && t.is_active) return false;
      if (pStatusFilter !== 'all' && t.personalization_status !== pStatusFilter) return false;
      if (!q) return true;
      return (
        t.crm_lead_name?.toLowerCase().includes(q) ||
        t.crm_lead_company?.toLowerCase().includes(q) ||
        t.lead_email?.toLowerCase().includes(q) ||
        t.location?.toLowerCase().includes(q)
      );
    });
  }, [tokens, search, statusFilter, pStatusFilter]);

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 8,
  });

  const allSelected = filtered.length > 0 && filtered.every((t) => selected.has(t.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((t) => t.id)));
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const bulk = useCallback(async (op: 'activate' | 'deactivate' | 'delete' | 'refresh' | 'recheck') => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    const ids = Array.from(selected);
    if (op === 'activate' || op === 'deactivate') {
      await supabase.from('tryout_tokens').update({ is_active: op === 'activate' }).in('id', ids);
    } else if (op === 'delete') {
      if (!confirm(`${ids.length} Leads wirklich löschen?`)) { setBulkLoading(false); return; }
      await supabase.from('tryout_tokens').delete().in('id', ids);
    } else if (op === 'refresh') {
      // Refresh = generate a new token string per row. Run in parallel so 1000
      // rows don't take 1000 sequential round-trips.
      await Promise.all(ids.map((id) => {
        const fresh = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '').slice(0, 16);
        return supabase.from('tryout_tokens').update({ token: fresh, usage_count: 0 }).eq('id', id);
      }));
    } else if (op === 'recheck') {
      // Reset selected to pending; the function picks up pending+failed for the list.
      await supabase.from('tryout_tokens')
        .update({ personalization_status: 'pending' }).in('id', ids);

      const supaUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`${supaUrl}/functions/v1/tryout-personalization-check`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token ?? ''}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ list_id: listId }),
      });
    }
    setSelected(new Set());
    await fetchList();
    setBulkLoading(false);
  }, [selected, fetchList, listId]);

  const exportCsv = useCallback(() => {
    const csv = buildInstantlyExportCsv(tokens.map((t) => ({
      lead_email: t.lead_email,
      lead_first_name: t.lead_first_name,
      crm_lead_name: t.crm_lead_name,
      crm_lead_company: t.crm_lead_company,
      location: t.location,
      bundesland: t.bundesland,
      berufsgruppe: t.berufsgruppe,
      personalization_original: t.personalization_original,
      campaign_name: list?.name ?? null,
      tryout_url: `${window.location.origin}/tryout/${t.token}`,
    })));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${list?.name ?? 'tryout-leads'}.csv`;
    a.click();
  }, [tokens, list]);

  if (loading) return <div className="p-8 flex items-center"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…</div>;
  if (!list) return <div className="p-8">Liste nicht gefunden.</div>;

  const activeCount = tokens.filter((t) => t.is_active).length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Button size="icon" variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-bold">{list.name}</h1>
        {list.campaign_tag && <span className="px-2 py-0.5 rounded-full bg-muted text-sm">{list.campaign_tag}</span>}
      </div>
      <div className="text-sm text-muted-foreground mb-4">
        {tokens.length} Leads · {activeCount} aktiv · {list.source}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" placeholder="Suche: Name, Firma, E-Mail, Stadt…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
          className="border rounded-md h-9 px-2 text-sm">
          <option value="all">Status: Alle</option>
          <option value="active">Aktiv</option>
          <option value="inactive">Deaktiviert</option>
        </select>
        <select value={pStatusFilter} onChange={(e) => setPStatusFilter(e.target.value)}
          className="border rounded-md h-9 px-2 text-sm">
          <option value="all">KI: Alle</option>
          <option value="pending">Pending</option>
          <option value="original_ok">Original OK</option>
          <option value="ai_generated">KI generiert</option>
          <option value="fallback_used">Fallback</option>
          <option value="failed">Fehler</option>
        </select>
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-4 w-4 mr-2" /> CSV-Export
        </Button>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 mb-3 p-2 rounded-md bg-muted">
          <span className="text-sm">{selected.size} markiert</span>
          <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulk('activate')}>
            <Play className="h-3 w-3 mr-1" /> Aktivieren
          </Button>
          <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulk('deactivate')}>
            <Pause className="h-3 w-3 mr-1" /> Deaktivieren
          </Button>
          <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulk('refresh')}>
            <RefreshCw className="h-3 w-3 mr-1" /> Tokens neu
          </Button>
          <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulk('recheck')}>
            <Sparkles className="h-3 w-3 mr-1" /> KI-Check
          </Button>
          <Button size="sm" variant="destructive" disabled={bulkLoading} onClick={() => bulk('delete')}>
            <Trash2 className="h-3 w-3 mr-1" /> Löschen
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-[40px_2fr_2fr_2fr_1fr_1fr_1fr_40px] gap-2 px-3 py-2 border-b text-xs font-medium uppercase text-muted-foreground">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            <div>Name</div>
            <div>Firma</div>
            <div>E-Mail</div>
            <div>Stadt</div>
            <div>Status</div>
            <div>Personalisierung</div>
            <div></div>
          </div>
          <div ref={parentRef} className="h-[600px] overflow-auto">
            <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
              {virtualizer.getVirtualItems().map((vi) => {
                const t = filtered[vi.index];
                return (
                  <div key={t.id}
                    className="grid grid-cols-[40px_2fr_2fr_2fr_1fr_1fr_1fr_40px] gap-2 px-3 py-2 items-center border-b text-sm hover:bg-muted/50"
                    style={{
                      position: 'absolute', top: 0, left: 0, right: 0,
                      transform: `translateY(${vi.start}px)`, height: vi.size,
                    }}>
                    <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleOne(t.id)} />
                    <div className="truncate">{t.crm_lead_name ?? '—'}</div>
                    <div className="truncate">{t.crm_lead_company ?? '—'}</div>
                    <div className="truncate text-muted-foreground">{t.lead_email ?? '—'}</div>
                    <div className="truncate">{t.location ?? '—'}</div>
                    <div>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${t.is_active ? 'bg-green-100 text-green-900' : 'bg-gray-100 text-gray-700'}`}>
                        {t.is_active ? 'Aktiv' : 'Aus'}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{t.personalization_status ?? '—'}</div>
                    <Button size="icon" variant="ghost" onClick={() => setDrawerToken(t)}>
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <TryoutLeadDrawer
        token={drawerToken}
        listId={listId}
        onClose={() => setDrawerToken(null)}
        onUpdated={() => { setDrawerToken(null); fetchList(); }}
      />
    </div>
  );
};
